import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Shield, Scale, Zap } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

function TermsOfService() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const styles = {
    container: {
      minHeight: '100vh',
      background: theme.colors.bg.primary,
      padding: '40px 20px',
      color: theme.colors.text.primary
    },
    wrapper: {
      maxWidth: '900px',
      margin: '0 auto',
      background: theme.colors.bg.secondary,
      borderRadius: '20px',
      padding: '40px',
      border: `1px solid ${theme.colors.border.primary}`
    },
    header: {
      marginBottom: '40px'
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: 'transparent',
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: '8px',
      padding: '8px 16px',
      color: theme.colors.text.secondary,
      cursor: 'pointer',
      fontSize: '14px',
      marginBottom: '20px',
      transition: 'all 0.2s'
    },
    title: {
      fontSize: '36px',
      fontWeight: '700',
      marginBottom: '10px',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent'
    },
    lastUpdated: {
      color: theme.colors.text.tertiary,
      fontSize: '14px'
    },
    section: {
      marginBottom: '32px'
    },
    sectionTitle: {
      fontSize: '24px',
      fontWeight: '600',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: theme.colors.text.primary
    },
    paragraph: {
      fontSize: '16px',
      lineHeight: '1.8',
      color: theme.colors.text.secondary,
      marginBottom: '16px'
    },
    list: {
      listStyle: 'none',
      padding: 0,
      marginBottom: '16px'
    },
    listItem: {
      fontSize: '16px',
      lineHeight: '1.8',
      color: theme.colors.text.secondary,
      marginBottom: '12px',
      paddingLeft: '24px',
      position: 'relative'
    },
    bullet: {
      position: 'absolute',
      left: 0,
      color: '#6366f1'
    },
    highlight: {
      background: 'rgba(239, 68, 68, 0.1)',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      marginBottom: '16px'
    },
    link: {
      color: '#6366f1',
      textDecoration: 'none',
      fontWeight: '500'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <button 
            style={styles.backButton}
            onClick={() => navigate(-1)}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <ArrowLeft size={16} />
            Back
          </button>
          
          <h1 style={styles.title}>Terms of Service</h1>
          <p style={styles.lastUpdated}>Last Updated: February 2, 2026</p>
        </div>

        {/* Introduction */}
        <div style={styles.section}>
          <p style={styles.paragraph}>
            Welcome to Bidoryx! These Terms of Service ("Terms") govern your use of our AI-powered Google Ads optimization platform. By accessing or using Bidoryx, you agree to be bound by these Terms.
          </p>
          <p style={styles.paragraph}>
            Please read these Terms carefully. If you do not agree with any part of these Terms, you may not use our service.
          </p>
        </div>

        {/* Acceptance of Terms */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <FileText size={24} style={{ color: '#6366f1' }} />
            1. Acceptance of Terms
          </h2>
          
          <p style={styles.paragraph}>
            By creating an account and using Bidoryx, you acknowledge that:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You are at least 18 years of age
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You have the authority to enter into these Terms
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You will comply with all applicable laws and regulations
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You have read and agree to our <a href="/privacy" style={styles.link}>Privacy Policy</a>
            </li>
          </ul>
        </div>

        {/* Description of Service */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <Zap size={24} style={{ color: '#f59e0b' }} />
            2. Description of Service
          </h2>
          
          <p style={styles.paragraph}>
            Bidoryx provides an AI-powered platform for Google Ads optimization, including:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Campaign creation and management:</strong> Build and optimize Google Ads campaigns
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Keyword research:</strong> Discover relevant keywords using Google's Keyword Planner API
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>AI-powered ad copy:</strong> Generate compelling headlines and descriptions
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Bid optimization:</strong> Automated bid adjustments based on performance
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Quality Score optimization:</strong> Improve ad relevance and performance
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Performance analytics:</strong> Track and analyze campaign performance
            </li>
          </ul>

          <p style={styles.paragraph}>
            Bidoryx acts as a tool to help you manage your Google Ads campaigns. <strong>You remain fully responsible for your advertising decisions and campaign performance.</strong>
          </p>
        </div>

        {/* Google Ads Account Requirements */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            3. Google Ads Account Requirements
          </h2>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You must have an active Google Ads account in good standing
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You must grant Bidoryx permission to access your Google Ads account via OAuth
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You must comply with <a href="https://support.google.com/adspolicy/answer/6008942" target="_blank" rel="noopener noreferrer" style={styles.link}>Google Ads Policies</a>
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You are responsible for all charges incurred in your Google Ads account
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You can revoke Bidoryx's access at any time through your Google Account settings
            </li>
          </ul>
        </div>

        {/* User Responsibilities */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <Shield size={24} style={{ color: '#10b981' }} />
            4. User Responsibilities
          </h2>
          
          <p style={styles.paragraph}>
            You agree to:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Provide accurate and complete information when creating your account
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Keep your account credentials secure and confidential
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Monitor your Google Ads campaigns regularly
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Review AI-generated content before publishing to Google Ads
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Comply with all applicable advertising laws and regulations
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Not use Bidoryx for any illegal or unauthorized purpose
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Not attempt to reverse engineer, decompile, or hack the service
            </li>
          </ul>
        </div>

        {/* AI-Generated Content Disclaimer */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <AlertTriangle size={24} style={{ color: '#ef4444' }} />
            5. AI-Generated Content Disclaimer
          </h2>
          
          <div style={styles.highlight}>
            <p style={{ ...styles.paragraph, marginBottom: 0, fontSize: '15px' }}>
              <strong>Important:</strong> Bidoryx uses AI (OpenAI GPT models) to generate ad copy, keywords, and recommendations. While we strive for quality:
            </p>
          </div>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              AI-generated content may contain errors, inaccuracies, or inappropriate language
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You are solely responsible for reviewing and approving all content before it goes live
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Bidoryx is not responsible for poor campaign performance due to AI suggestions
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You must ensure all ads comply with Google Ads policies and local advertising laws
            </li>
          </ul>

          <p style={styles.paragraph}>
            <strong>We strongly recommend:</strong> Always review AI-generated headlines, descriptions, and keywords before publishing. Use Bidoryx as a tool to assist your advertising decisions, not replace your judgment.
          </p>
        </div>

        {/* Subscription and Billing */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            6. Subscription and Billing
          </h2>
          
          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            Subscription Plans
          </h3>
          <p style={styles.paragraph}>
            Bidoryx offers multiple subscription tiers (Starter, Professional, Enterprise) with different features and usage limits. Current pricing is available at <a href="/subscription" style={styles.link}>app.bidoryx.com/subscription</a>.
          </p>

          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            Payment Terms
          </h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Subscriptions are billed monthly or annually (depending on your plan)
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Payments are processed securely through Stripe
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              All fees are in EUR unless otherwise stated
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Subscriptions automatically renew unless cancelled
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You can upgrade, downgrade, or cancel your subscription at any time
            </li>
          </ul>

          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            Refund Policy
          </h3>
          <p style={styles.paragraph}>
            Refunds are handled on a case-by-case basis. If you're unsatisfied with the service within the first 14 days, contact <a href="mailto:support@bidoryx.com" style={styles.link}>support@bidoryx.com</a> to discuss a refund.
          </p>
        </div>

        {/* Usage Limits */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            7. Usage Limits and Fair Use
          </h2>
          
          <p style={styles.paragraph}>
            Each subscription plan includes usage limits (e.g., number of campaigns, API calls, ad spend managed). You agree to:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Use the service within the limits of your subscription tier
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Not abuse or excessively burden our infrastructure
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Upgrade to a higher tier if you exceed your current plan's limits
            </li>
          </ul>

          <p style={styles.paragraph}>
            We reserve the right to throttle or suspend accounts that violate fair use policies.
          </p>
        </div>

        {/* Limitation of Liability */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <Scale size={24} style={{ color: '#8b5cf6' }} />
            8. Limitation of Liability
          </h2>
          
          <div style={styles.highlight}>
            <p style={{ ...styles.paragraph, marginBottom: 0, fontSize: '15px' }}>
              <strong>IMPORTANT LEGAL NOTICE:</strong>
            </p>
          </div>
          
          <p style={styles.paragraph}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Bidoryx is provided "as is" without warranties of any kind
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              We do not guarantee specific campaign performance, ROI, or results
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              We are not liable for any loss of revenue, profits, or data
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              We are not responsible for Google Ads account suspensions or policy violations
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Our total liability is limited to the amount you paid in the last 12 months
            </li>
          </ul>

          <p style={styles.paragraph}>
            <strong>You acknowledge:</strong> Google Ads campaigns involve risk. Performance depends on many factors outside our control (market competition, audience behavior, product quality, etc.). Bidoryx provides tools and recommendations, but ultimate responsibility lies with you.
          </p>
        </div>

        {/* Intellectual Property */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            9. Intellectual Property
          </h2>
          
          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            Our Rights
          </h3>
          <p style={styles.paragraph}>
            All rights, title, and interest in Bidoryx (including software, algorithms, designs, logos, and trademarks) are owned by us. You may not copy, modify, distribute, or reverse engineer any part of the service.
          </p>

          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            Your Content
          </h3>
          <p style={styles.paragraph}>
            You retain ownership of your campaign data, ad copy, and business information. By using Bidoryx, you grant us a limited license to process and store your data solely to provide the service.
          </p>
        </div>

        {/* Data Protection and Privacy */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            10. Data Protection and Privacy
          </h2>
          
          <p style={styles.paragraph}>
            Your privacy is important to us. Our data practices are governed by our <a href="/privacy" style={styles.link}>Privacy Policy</a>, which is incorporated into these Terms by reference.
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              We encrypt all data in transit and at rest
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              We comply with GDPR for EU users
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You can export or delete your data at any time
            </li>
          </ul>
        </div>

        {/* Termination */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            11. Termination
          </h2>
          
          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            Your Right to Terminate
          </h3>
          <p style={styles.paragraph}>
            You may cancel your subscription and close your account at any time through the Settings page or by contacting support.
          </p>

          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            Our Right to Terminate
          </h3>
          <p style={styles.paragraph}>
            We reserve the right to suspend or terminate your account if:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You violate these Terms
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You engage in fraudulent or illegal activity
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Your payment fails or your account is past due
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              You abuse the service or violate fair use policies
            </li>
          </ul>

          <p style={styles.paragraph}>
            Upon termination, your access to Bidoryx will be revoked, but we may retain your data as required by law or our Privacy Policy.
          </p>
        </div>

        {/* Changes to Terms */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            12. Changes to These Terms
          </h2>
          
          <p style={styles.paragraph}>
            We may update these Terms from time to time. When we make significant changes, we will:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Update the "Last Updated" date at the top of this page
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Notify you via email or in-app notification
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Require you to accept the updated Terms to continue using the service
            </li>
          </ul>

          <p style={styles.paragraph}>
            Continued use of Bidoryx after changes constitutes acceptance of the updated Terms.
          </p>
        </div>

        {/* Governing Law */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            13. Governing Law and Disputes
          </h2>
          
          <p style={styles.paragraph}>
            These Terms are governed by the laws of the Netherlands. Any disputes will be resolved through:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Good faith negotiation between the parties
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Mediation or arbitration (if negotiation fails)
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Courts in the Netherlands (as a last resort)
            </li>
          </ul>
        </div>

        {/* Contact Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            14. Contact Us
          </h2>
          
          <p style={styles.paragraph}>
            If you have questions about these Terms, please contact us:
          </p>
          
          <div style={{ ...styles.highlight, background: 'rgba(99, 102, 241, 0.1)', border: `1px solid ${theme.colors.border.primary}` }}>
            <p style={{ ...styles.paragraph, marginBottom: '8px' }}>
              <strong>Email:</strong> <a href="mailto:legal@bidoryx.com" style={styles.link}>legal@bidoryx.com</a>
            </p>
            <p style={{ ...styles.paragraph, marginBottom: '8px' }}>
              <strong>Support:</strong> <a href="mailto:support@bidoryx.com" style={styles.link}>support@bidoryx.com</a>
            </p>
            <p style={{ ...styles.paragraph, marginBottom: 0 }}>
              <strong>Website:</strong> <a href="https://app.bidoryx.com" style={styles.link}>https://app.bidoryx.com</a>
            </p>
          </div>
        </div>

        {/* Entire Agreement */}
        <div style={styles.section}>
          <p style={{ ...styles.paragraph, fontSize: '14px', color: theme.colors.text.tertiary, marginBottom: 0 }}>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and Bidoryx. If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will remain in full force and effect.
          </p>
        </div>

        {/* Acceptance */}
        <div style={{ ...styles.highlight, background: 'rgba(16, 185, 129, 0.1)', border: `1px solid rgba(16, 185, 129, 0.3)`, marginTop: '32px' }}>
          <p style={{ ...styles.paragraph, marginBottom: 0, fontSize: '15px' }}>
            <strong>By using Bidoryx, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</strong>
          </p>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
